import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Flex,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Input,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import type { IConfigureStepProps, TConfigurationValues } from './types';
import {
  AddConnectionDialog,
  useConnectionActions,
} from '@/modules/integrations';
import {
  useRememberTriggerForResume,
  useTriggerOAuthConnection,
} from '@/modules/triggers/hooks/use-trigger-oauth-connection';
import { WebhookConfiguration } from '../WebhookTriggerDialog/WebhookConfiguration';
import { ConfigFormRow } from '../WebhookTriggerDialog/ConfigFormRow';
import { extractTriggerFieldGroups } from '../WebhookTriggerDialog/utils';
import { POLL_INTERVAL_MIN, POLL_INTERVAL_MAX } from './constants';
import { useTriggerConfigureData } from '../shared/useTriggerConfigureData';
import { IntegrationSelectRow } from '../shared/IntegrationSelectRow';
import { ConnectionSelectRow } from '../shared/ConnectionSelectRow';
import type { V2BookPollDescriptor } from '@/generated/hooks/bdk/models';

/**
 * Poll configuration keys the runtime manages itself (not entered by the user),
 * hidden from the dialog's dynamic configuration form.
 */
const HIDDEN_POLL_CONFIG_KEYS = ['last_checkpoint'];

/**
 * ConfigureStep Component
 *
 * The first step of the poll trigger dialog where users select
 * the integration, connection, event and interval for the poll.
 *
 * @param props - Component props
 * @returns JSX element containing the configuration form
 */
export function ConfigureStep({
  selectedIntegration,
  onIntegrationChange,
  onIntegrationVersionChange,
  selectedConnection,
  onConnectionChange,
  selectedPollId,
  onSelectedPollChange,
  selectedPollIntervalMinutes,
  onSelectedPollIntervalMinutesChange,
  configurationValues,
  onConfigurationChange,
  onTriggerFieldGroupsExtracted,
  onSelectedIntegrationDisplayNameChange,
  onFillableConfigKeysChange,
  highlightedConfigKey,
  highlightConfigNonce,
  readOnly = false,
  hasNextStep = false,
}: IConfigureStepProps) {
  const { t } = useTranslation('automation-inputs');

  // Shared integration + connection data logic
  const {
    organizationId,
    workspaceId,
    uniqueIntegrations,
    isLoadingIntegrations,
    isLoadingConnections,
    draftConnections,
    selectedIntegrationData,
    handleConnectionChange,
    isAddConnectionDialogOpen,
    openAddConnectionDialog,
    closeAddConnectionDialog,
    handleConnectionSuccess,
    latestIntegrationVersion,
  } = useTriggerConfigureData({
    selectedIntegration,
    selectedConnection,
    onConnectionChange,
    onIntegrationVersionChange,
    booksFilterField: 'has_polls',
  });

  // Connection actions for AddConnectionDialog
  const {
    createConnection,
    updateConnection,
    createOAuthAuthorizationConnection,
  } = useConnectionActions(
    organizationId,
    workspaceId,
    selectedIntegration,
    latestIntegrationVersion,
  );

  const rememberForResume = useRememberTriggerForResume({
    triggerKind: 'poll',
    integration: selectedIntegrationData,
  });
  const handleOAuthClick = useTriggerOAuthConnection({
    triggerKind: 'poll',
    integration: selectedIntegrationData,
    createOAuthAuthorizationConnection,
  });

  // Get polls from the selected integration
  const polls = useMemo<V2BookPollDescriptor[]>(
    () => selectedIntegrationData?.polls ?? [],
    [selectedIntegrationData?.polls],
  );

  // Find the currently selected poll
  const selectedPoll = useMemo(
    () => polls.find(p => p.id === selectedPollId),
    [polls, selectedPollId],
  );

  // Framework-managed poll parameters the runtime sets itself (e.g. the resume
  // cursor), so they are never shown as user-editable fields in the dialog.
  const visibleConfiguration = useMemo<V2BookPollDescriptor['configuration']>(
    () =>
      selectedPoll?.configuration
        ? Object.fromEntries(
            Object.entries(selectedPoll.configuration).filter(
              ([key]) => !HIDDEN_POLL_CONFIG_KEYS.includes(key),
            ),
          )
        : undefined,
    [selectedPoll?.configuration],
  );

  // Default empty configuration values
  const defaultConfigValues: TConfigurationValues = {};

  /**
   * Handles poll selection change.
   * Extracts trigger field groups from the selected poll's event_type
   * and passes them to the parent component so the Map Inputs step can
   * populate its dropdown.
   */
  const handlePollChange = useCallback(
    (pollId: string) => {
      onSelectedPollChange?.(pollId);

      const nextPoll = polls.find(p => p.id === pollId);
      onTriggerFieldGroupsExtracted?.(extractTriggerFieldGroups(nextPoll));
    },
    [polls, onSelectedPollChange, onTriggerFieldGroupsExtracted],
  );

  // In edit/readOnly mode the poll is pre-selected and the Select is disabled,
  // so handlePollChange never fires. Extract the field groups here once the
  // poll data resolves for that pre-selected poll. Create mode is handled
  // directly by handlePollChange, so this is gated on readOnly to avoid a
  // redundant extraction when the user actively picks a poll.
  // eslint-disable-next-line no-restricted-syntax -- reason: Need to extract poll fields when edit/readOnly mode opens with a pre-selected poll
  useEffect(() => {
    if (readOnly && selectedPoll) {
      onTriggerFieldGroupsExtracted?.(extractTriggerFieldGroups(selectedPoll));
    }
  }, [readOnly, selectedPoll, onTriggerFieldGroupsExtracted]);

  // Bubble the integration's display name up so the dialog can title the
  // assistant thread with it rather than the raw book id.
  // eslint-disable-next-line no-restricted-syntax -- reason: notify parent when resolved integration changes
  useEffect(() => {
    onSelectedIntegrationDisplayNameChange?.(
      selectedIntegrationData?.display_name,
    );
  }, [selectedIntegrationData, onSelectedIntegrationDisplayNameChange]);

  // Bubble up the keys the user can actually fill (visible config, hidden
  // runtime-managed keys excluded) so the dialog can reject a set_trigger_field
  // call from the assistant for any other key.
  // eslint-disable-next-line no-restricted-syntax -- reason: notify parent when the fillable config schema changes
  useEffect(() => {
    onFillableConfigKeysChange?.(Object.keys(visibleConfiguration ?? {}));
  }, [visibleConfiguration, onFillableConfigKeysChange]);

  const [intervalTouched, setIntervalTouched] = useState(false);

  const intervalError = useMemo(() => {
    if (!intervalTouched || selectedPollIntervalMinutes === undefined)
      return undefined;
    if (
      selectedPollIntervalMinutes < POLL_INTERVAL_MIN ||
      selectedPollIntervalMinutes > POLL_INTERVAL_MAX
    ) {
      return t('pollDialog.configure.selectPollInterval.error');
    }
    return undefined;
  }, [intervalTouched, selectedPollIntervalMinutes, t]);

  /**
   * Handles poll interval change.
   */
  const handlePollIntervalChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIntervalTouched(true);
      if (e.target.value === '') {
        onSelectedPollIntervalMinutesChange?.(undefined);
        return;
      }
      const value = parseInt(e.target.value, 10);
      if (!isNaN(value)) {
        onSelectedPollIntervalMinutesChange?.(value);
      }
    },
    [onSelectedPollIntervalMinutesChange],
  );

  return (
    <Flex direction="column" gap={2} className="rounded-lg w-full">
      <div className="bg-secondary rounded-lg p-5 w-full">
        {/* Select Integration */}
        <IntegrationSelectRow
          label={t('pollDialog.configure.selectIntegration.label')}
          description={t('pollDialog.configure.selectIntegration.description')}
          placeholder={t('pollDialog.configure.selectIntegration.placeholder')}
          selectedIntegration={selectedIntegration}
          onIntegrationChange={onIntegrationChange}
          uniqueIntegrations={uniqueIntegrations}
          isLoading={isLoadingIntegrations}
          disabled={readOnly}
        />

        {/* Select Connection */}
        <ConnectionSelectRow
          label={t('pollDialog.configure.selectConnection.label')}
          description={t('pollDialog.configure.selectConnection.description')}
          placeholder={t('pollDialog.configure.selectConnection.placeholder')}
          newConnectionLabel={t(
            'pollDialog.configure.selectConnection.newConnection',
          )}
          inactiveLabel={t(
            hasNextStep
              ? 'trigger.connection.inactiveContinue'
              : 'trigger.connection.inactiveSave',
          )}
          selectedIntegration={selectedIntegration}
          selectedConnection={selectedConnection}
          onConnectionChange={handleConnectionChange}
          draftConnections={draftConnections}
          isLoading={isLoadingConnections || isLoadingIntegrations}
          lockSelections={readOnly}
          onAddConnection={openAddConnectionDialog}
        />

        {/* Select Poll */}
        <ConfigFormRow
          label={t('pollDialog.configure.selectPoll.label')}
          description={t('pollDialog.configure.selectPoll.description')}
          disabled={!selectedConnection}
        >
          {isLoadingIntegrations ? (
            <Skeleton className="h-9 w-full rounded-md bg-alternate-1" />
          ) : (
            <Select
              value={selectedPollId}
              onValueChange={handlePollChange}
              disabled={!selectedConnection || readOnly}
            >
              <SelectTrigger className="w-full bg-background">
                <SelectValue
                  placeholder={t('pollDialog.configure.selectPoll.placeholder')}
                />
              </SelectTrigger>
              <SelectContent>
                {polls.map(poll => (
                  <SelectItem key={poll.id} value={poll.id ?? ''}>
                    {poll.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </ConfigFormRow>

        {/* Poll Interval */}
        <ConfigFormRow
          label={t('pollDialog.configure.selectPollInterval.label')}
          description={t('pollDialog.configure.selectPollInterval.description')}
          disabled={!selectedPollId}
          error={intervalError}
        >
          <Input
            type="number"
            min={POLL_INTERVAL_MIN}
            max={POLL_INTERVAL_MAX}
            value={selectedPollIntervalMinutes}
            onChange={handlePollIntervalChange}
            disabled={!selectedPollId || readOnly}
          />
        </ConfigFormRow>
      </div>

      {/* Poll Configuration - Render when poll has user-facing configuration */}
      {visibleConfiguration && Object.keys(visibleConfiguration).length > 0 && (
        <WebhookConfiguration
          configuration={visibleConfiguration}
          values={configurationValues ?? defaultConfigValues}
          onValueChange={(key, value) => onConfigurationChange?.(key, value)}
          readOnly={readOnly}
          highlightedKey={highlightedConfigKey}
          highlightNonce={highlightConfigNonce}
        />
      )}

      {/* Add Connection Dialog */}
      {selectedIntegrationData && (
        <AddConnectionDialog
          isOpen={isAddConnectionDialogOpen}
          onCancel={closeAddConnectionDialog}
          onSuccess={handleConnectionSuccess}
          onOAuthClick={handleOAuthClick}
          onManagedSignInRedirect={rememberForResume}
          integration={selectedIntegrationData}
          createConnection={createConnection}
          updateConnection={updateConnection}
        />
      )}
    </Flex>
  );
}
